/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { useCallback, useContext } from "react";
import Config from "data/config";
import { checkDiffs, getDiff, prepareChangeData } from "utils/change-detection";
import { useModel } from "cms/models/context/use-model";
import { getFieldConfiguration } from "cms/fields/field-utils";
import { FieldZoneContext } from "cms/fields/context/provider";
import { Logger } from "cms/utils/log/logger";
import { useUpdateModel } from "hooks/use-update-model";

const useFieldZone = () => {

    const fieldZoneContext = useContext(FieldZoneContext);
    const FieldZoneState = fieldZoneContext?.state;
    const FieldZoneDispatch = fieldZoneContext?.dispatch;
    const expanded = FieldZoneState?.expanded;
    const { ModelState, initFieldItem } = useModel();
    const updateModel = useUpdateModel();
    const mode = ModelState?.mode;

    const setFieldDisabled = (value) => {
        initZoneField("disabled", value);
    };
    
    const initZoneField = (name, value) => {
        return FieldZoneDispatch({ type: "SET_ZONE_FIELD_ITEM", name, value });
    };

    const getFieldConfig = (type) => {
        return getFieldConfiguration(type);
    };

    const toggleMode = (event) => {
        event.stopPropagation();
        if (mode === "display") {
            if (!expanded) {
                initZoneField("expanded", true);
            }
            //  update Model's mode
            initFieldItem("mode", "edit");
        } else {
            initFieldItem("mode", "display");
        }
    };

    const toggleExpanded = () => {
        initZoneField("expanded", !expanded);
    };

    const handleUpdateField = useCallback(
        (field) => updateModel({ field }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );
    
    const applyFieldChanges = (field, updates) => {
        const TAG = "[applyFieldChanges]";
        // merge field with updates
        const tmpUpdatedField = {
            ...field,
            ...updates,
        };
        // cleans both objects before diffing
        const { original, modified } = prepareChangeData(field, tmpUpdatedField);
        if (Config.Logging.enableSavePipelineLog) {
            Logger.debug(`${TAG} original`, { original });
            Logger.debug(`${TAG} modified`, { modified });
        }
        const diffDetected = checkDiffs(original, modified, "applyFieldChanges");
        if (Config.Logging.enableSavePipelineLog) {
            Logger.debug(`${TAG} - is dirty: `, { diffDetected });
        }
        if (diffDetected) {
            if (Config.Logging.enableSavePipelineLog) {
                Logger.debug(`${TAG} - saving change.`);
            }
            const diff = getDiff(original, modified);
            Logger.debug(`${TAG} diff: `, { diff });
            
            initZoneField("isDirty", diffDetected);
            handleUpdateField(modified);

            // TODO: possibly call updateModel directly rather than handleUpdateField
            //   but also needs useCallback?
            // updateModel({ field })

        } else {
            if (Config.Logging.enableSavePipelineLog) {
                Logger.debug(`${TAG} - NO change detected:`);
            }
        }
    };

    return {
        FieldZoneState,
        FieldZoneDispatch,
        initZoneField,
        toggleMode,
        toggleExpanded,
        applyFieldChanges,
        getFieldConfig,
        setFieldDisabled,
    };
};

export { useFieldZone };
